import { logNetworkRequest } from 'Diagnostics/diagnostics';
import { CustomErrorType } from 'Models/index';
import { normalizePageSize } from 'Models/pagination';
import requestConfig from 'Models/uiConfig/requestConfig';
import type { RequestExtra, SearchRequestDefaults } from 'Models/uiConfig/requestTypes';
import { convertibleFromObject } from 'Modules/converter/index';
import { correctionLevelToParam } from 'Modules/serverApi/common/flags';

import { facetsToConvertible, sortToConvertible } from './common/convert';
import { asCmError, GET, GETBACKUP, getErrorSearchResponse } from './common/httpClient';
import ResponseValidationError from './common/responseValidationError';
import { getSearchResponseValidationError, isSearchResponseShape } from './common/validators';
import { SEARCH_ENDPOINT } from './endpoints';
import { SEARCH_RESPONSE_ERROR_STATES, type SearchRequest, type ServerModel } from './types';

export { isSearchResponseShape as isSearchResponse } from './common/validators';

type ResponseContext = Pick<ServerModel.SearchResponse, 'Facets' | 'Messages' | 'Extra'>;

// Keep raw context isolated from response handlers' mutations.
let fullResponseContext: { requestKey: string; context: ResponseContext } | undefined;

const { requestDefaults, responseHandler } = requestConfig;

function getStringParam(value: string | undefined, defaultValue?: string): string | undefined {
  return value ?? defaultValue;
}

function getNumberParam(value: number | undefined, defaultValue?: number): number | undefined {
  return value ?? defaultValue;
}

function getPageSizeParam(request: SearchRequest, defaults: SearchRequestDefaults): number | undefined {
  return (
    request.pageSize ??
    (defaults.pageSize === undefined ? undefined : normalizePageSize(defaults.pageSize).pageSize)
  );
}

export default async function search(
  request: SearchRequest,
  abortSignal?: AbortSignal,
  onInit?: () => void,
): Promise<ServerModel.SearchResponse> {
  const extra = {
    ...(requestDefaults.extra ?? {}),
    ...(request.extra ?? {}),
  };

  const params: RequestExtra = {
    query: encodeURIComponent(request.query ?? requestDefaults.query ?? ''),
    hiddenquery: encodeURIComponent(request.hiddenQuery ?? requestDefaults.hiddenQuery ?? ''),
    filterquery: encodeURIComponent(request.filterQuery ?? requestDefaults.filterQuery ?? ''),
    querycorrectionlevel: correctionLevelToParam(
      request.queryCorrectionLevel,
      requestDefaults.queryCorrectionLevel,
    ),
    page: getNumberParam(request.pageNumber, requestDefaults.pageNumber),
    pagesize: getPageSizeParam(request, requestDefaults),
    mode: encodeURIComponent(request.mode ?? requestDefaults.mode ?? ''),
    sort: sortToConvertible(request.sort ?? requestDefaults.sort ?? null),
    catalog: encodeURIComponent(request.catalog ?? requestDefaults.catalog ?? ''),
    facet: facetsToConvertible(request, requestDefaults),
    extra: await convertibleFromObject(extra),
    variantsMode: encodeURIComponent(request.variantsMode ?? requestDefaults.variantsMode ?? ''),
    referrer: getStringParam(request.referrer, requestDefaults.referrer),
    ...(request.explainScores ? { explainscores: 'true' } : {}),
  };

  const requestKey = JSON.stringify({ ...params, page: undefined, pagesize: undefined, sort: undefined });
  const context =
    request.preferProductsOnly && fullResponseContext?.requestKey === requestKey
      ? fullResponseContext.context
      : undefined;
  if (context) {
    params.scope = 'Products';
    params.facet = facetsToConvertible({ ...request, maxCountByField: {} }, requestDefaults);
  }
  // Capture context before the network wait; another caller may replace the cache.
  const responseContext = { requestKey, context, abortSignal };

  try {
    return getSearchResponse(
      await GET(SEARCH_ENDPOINT, params, { signal: abortSignal }),
      onInit,
      responseContext,
    );
  } catch (primaryErr) {
    const pe = asCmError(primaryErr);

    if (pe.type === CustomErrorType.requestAborted) {
      return getErrorSearchResponse(primaryErr);
    }

    console.warn(`The search request failed: ${pe.message}`);

    if (pe.status === 429) {
      return getErrorSearchResponse(primaryErr);
    }

    try {
      const backupResponse = getSearchResponse(
        await GETBACKUP(SEARCH_ENDPOINT, params, { signal: abortSignal }),
        onInit,
        responseContext,
      );
      logNetworkRequest({ endpoint: SEARCH_ENDPOINT, scenario: 'backup_used', primaryError: pe });
      return backupResponse;
    } catch (backupErr) {
      const be = asCmError(backupErr);
      logNetworkRequest({
        endpoint: SEARCH_ENDPOINT,
        scenario: 'backup_failed',
        primaryError: pe,
        backupError: be,
      });
      const message = await getErrorSearchResponse(backupErr);
      return message;
    }
  }
}

function getSearchResponse(
  value: unknown,
  onInit: (() => void) | undefined,
  {
    requestKey,
    context,
    abortSignal,
  }: {
    requestKey: string;
    context?: ResponseContext;
    abortSignal?: AbortSignal;
  },
): ServerModel.SearchResponse {
  if (!isSearchResponseShape(value)) {
    const validationError = getSearchResponseValidationError(value) ?? 'unknown validation error';
    throw new ResponseValidationError(`${SEARCH_ENDPOINT} response validation failed: ${validationError}`);
  }

  const isError = SEARCH_RESPONSE_ERROR_STATES.some((state) => state === value.State);
  const rawContext = context ?? { Facets: value.Facets, Messages: value.Messages, Extra: value.Extra };
  const copiedContext = isError
    ? undefined
    : { ...rawContext, Extra: { ...rawContext.Extra, ...value.Extra } };

  // Copy nested raw objects before handlers can change them.
  const pending: object[] = copiedContext ? [copiedContext] : [];
  for (const target of pending) {
    for (const [key, entry] of Object.entries(target)) {
      if (entry !== null && typeof entry === 'object') {
        const copy = Array.isArray(entry) ? [...entry] : { ...entry };
        Object.assign(target, { [key]: copy });
        pending.push(copy);
      }
    }
  }

  const response = copiedContext ? { ...value, ...copiedContext } : value;
  const processed = responseHandler(response, onInit);

  if (
    context === undefined &&
    !isError &&
    !abortSignal?.aborted &&
    !SEARCH_RESPONSE_ERROR_STATES.some((state) => state === processed.State)
  ) {
    fullResponseContext = { requestKey, context: rawContext };
  }

  return processed;
}
